import { useCallback, useEffect, useState } from 'react';
import { Plus, Copy, RotateCw, Key, AlertTriangle } from 'lucide-react';
import { apiOAuthClients } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import DatePicker from '../components/DatePicker.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';

/**
 * Sprint 10 — Vue admin des Clients OAuth M2M.
 *
 * Permet à un ADMIN de :
 *  - Créer un client → reçoit le `clientSecret` en clair UNE SEULE FOIS
 *    (modale spéciale "révélation" avec bouton Copier + avertissement).
 *  - Lister, modifier (nom, scopes, enabled, expiresAt), archiver, restaurer.
 *  - Roter le secret (nouveau secret également révélé une seule fois).
 */

// Doit rester aligné avec api/src/lib/scopes.ts (OAUTH_SCOPES).
const OAUTH_SCOPES = [
  { value: 'persons:read', label: 'Personnes (lecture)' },
  { value: 'persons:write', label: 'Personnes (écriture)' },
  { value: 'timeoffs:read', label: 'Congés (lecture)' },
  { value: 'timeoffs:write', label: 'Congés (écriture)' },
  { value: 'timeoffs:approve', label: 'Congés (validation)' },
  { value: 'clients:read', label: 'Clients (lecture)' },
  { value: 'clients:write', label: 'Clients (écriture)' },
  { value: 'affaires:read', label: 'Affaires (lecture)' },
  { value: 'affaires:write', label: 'Affaires (écriture)' },
  { value: 'roles:read', label: 'Rôles (lecture)' },
  { value: 'departments:read', label: 'Départements (lecture)' },
  { value: 'timeofftypes:read', label: 'Types de congé (lecture)' },
  { value: 'publicholidays:read', label: 'Jours fériés (lecture)' },
  { value: 'allocations:read', label: 'Allocations (lecture)' },
  { value: 'allocations:write', label: 'Allocations (création)' },
  { value: 'worklocations:read', label: 'Statuts de présence (lecture)' },
  { value: 'loggedtime:read', label: 'Temps saisis (lecture)' },
  { value: 'budget:read', label: 'Budget & alertes (lecture)' },
  { value: 'capacity:read', label: 'Capacité nette (lecture)' },
];

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const EMPTY_FORM = { name: '', scopes: [], enabled: true, expiresAt: '' };

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export default function OAuthClientsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // État de la modale "Révélation du secret" (création ou rotation).
  const [revealOpen, setRevealOpen] = useState(false);
  const [reveal, setReveal] = useState(null); // { clientName, clientId, clientSecret, mode: 'create' | 'rotate' }
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiOAuthClients.list(archivedFilter);
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    const scopes = Array.isArray(c.allowedScopes) ? c.allowedScopes : [];
    setForm({
      name: c.name,
      scopes,
      enabled: c.enabled,
      expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        await apiOAuthClients.update(editing.id, {
          name: form.name.trim(),
          scopes: form.scopes,
          enabled: form.enabled,
          expiresAt: form.expiresAt || null,
        });
        setModalOpen(false);
        await reload();
      } else {
        const { client, clientSecret } = await apiOAuthClients.create({
          name: form.name.trim(),
          scopes: form.scopes,
          expiresAt: form.expiresAt || null,
        });
        setModalOpen(false);
        setReveal({
          clientName: client.name,
          clientId: client.clientId,
          clientSecret,
          mode: 'create',
        });
        setRevealOpen(true);
        await reload();
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (c) => {
    if (!window.confirm(`Archiver le client "${c.name}" ? Toute requête authentifiée avec ce client sera refusée.`)) return;
    await apiOAuthClients.archive(c.id);
    await reload();
  };

  const onRestore = async (c) => {
    await apiOAuthClients.restore(c.id);
    await reload();
  };

  const onRotate = async (c) => {
    if (
      !window.confirm(
        `Rotater le secret du client "${c.name}" ? L'ancien secret cessera immédiatement de fonctionner.`,
      )
    )
      return;
    try {
      const { client, clientSecret } = await apiOAuthClients.rotateSecret(c.id);
      setReveal({
        clientName: client.name,
        clientId: client.clientId,
        clientSecret,
        mode: 'rotate',
      });
      setRevealOpen(true);
      await reload();
    } catch (err) {
      window.alert(err.message);
    }
  };

  const toggleScope = (scope) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(scope)
        ? f.scopes.filter((s) => s !== scope)
        : [...f.scopes, scope],
    }));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Pas critique — l'utilisateur peut copier à la main.
    }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Clients OAuth</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <button type="button" onClick={openCreate} style={{ ...primaryBtn, marginLeft: 'auto' }}>
          <Plus size={14} /> Nouveau client
        </button>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        Les clients OAuth permettent à des systèmes externes (autres apps Synotis) d'appeler
        l'API publique <code>/api/v1/public/…</code> via le flow <code>client_credentials</code>.
        Le <code>client_secret</code> n'est lisible qu'une seule fois, à la création ou après rotation.
      </p>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Nom</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Client ID</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Secret</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Scopes</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>État</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Dernière utilisation</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucun client OAuth.</td>
              </tr>
            ) : (
              items.map((c) => {
                const scopes = Array.isArray(c.allowedScopes) ? c.allowedScopes : [];
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '10px 12px', color: 'var(--text)', fontWeight: 500 }}>
                      {c.name}
                      {c.archivedAt ? (
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                          (archivé)
                        </span>
                      ) : null}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        color: 'var(--text-muted)',
                        fontFamily: 'monospace',
                        fontSize: 12,
                      }}
                    >
                      {c.clientId}
                    </td>
                    <td
                      style={{
                        padding: '10px 12px',
                        color: 'var(--text-muted)',
                        fontFamily: 'monospace',
                        fontSize: 12,
                      }}
                    >
                      {c.clientSecretPrefix}…
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {scopes.length === 0 ? (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>aucun</span>
                        ) : (
                          scopes.map((s) => (
                            <span
                              key={s}
                              style={{
                                fontSize: 11,
                                padding: '2px 6px',
                                background: 'var(--glass)',
                                border: '1px solid var(--glass-border)',
                                borderRadius: 'var(--radius-pill)',
                                color: 'var(--text)',
                                fontFamily: 'monospace',
                              }}
                            >
                              {s}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span
                        style={{
                          fontSize: 12,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-pill)',
                          background: c.enabled ? 'var(--success)' : 'var(--text-muted)',
                          color: '#fff',
                        }}
                      >
                        {c.enabled ? 'Actif' : 'Désactivé'}
                      </span>
                      {c.expiresAt ? (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                          expire le {formatDate(c.expiresAt)}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 12 }}>
                      {formatDate(c.lastUsedAt)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        {!c.archivedAt && (
                          <button
                            type="button"
                            onClick={() => onRotate(c)}
                            title="Rotater le secret"
                            aria-label="Rotater le secret"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-sm)',
                              padding: 6,
                              color: 'var(--warning)',
                              cursor: 'pointer',
                              display: 'inline-flex',
                            }}
                          >
                            <RotateCw size={14} />
                          </button>
                        )}
                        <EntityActions
                          archived={Boolean(c.archivedAt)}
                          onEdit={() => openEdit(c)}
                          onArchive={() => onArchive(c)}
                          onRestore={() => onRestore(c)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* --- Modale création / édition --- */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Modifier ${editing.name}` : 'Nouveau client OAuth'}
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="oauth-client-form"
              disabled={submitting || !form.name.trim() || form.scopes.length === 0}
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              {editing ? 'Enregistrer' : 'Créer et révéler le secret'}
            </button>
          </>
        }
      >
        <form id="oauth-client-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Nom
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={128}
              placeholder="Sync interfaceinterne"
              autoFocus
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Scopes autorisés ({form.scopes.length} sélectionné{form.scopes.length > 1 ? 's' : ''})
            </label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                background: 'var(--glass)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                padding: 10,
              }}
            >
              {OAUTH_SCOPES.map((s) => {
                const active = form.scopes.includes(s.value);
                return (
                  <button
                    type="button"
                    key={s.value}
                    onClick={() => toggleScope(s.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 10px',
                      background: active ? 'var(--accent)' : 'transparent',
                      color: active ? '#fff' : 'var(--text)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 12,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 11,
                        opacity: 0.8,
                        minWidth: 130,
                      }}
                    >
                      {s.value}
                    </span>
                    <span style={{ fontSize: 12 }}>{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {editing && (
            <div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}>
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                Activé (le client peut s'authentifier et appeler l'API)
              </label>
            </div>
          )}

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Date d'expiration (optionnel)
            </label>
            <DatePicker
              value={form.expiresAt}
              onChange={(v) => setForm({ ...form, expiresAt: v })}
              style={{ ...inputStyle, maxWidth: 200 }}
            />
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
              Laissez vide pour un client sans expiration. Recommandé : 90 à 365 jours.
            </div>
          </div>

          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
      </Modal>

      {/* --- Modale "Révélation du secret" --- */}
      <Modal
        open={revealOpen}
        onClose={() => setRevealOpen(false)}
        closeOnBackdrop={false}
        size="lg"
        title={reveal?.mode === 'rotate' ? 'Nouveau secret généré' : 'Client OAuth créé'}
        subtitle="Ce secret ne sera plus jamais affiché. Copiez-le maintenant."
        footer={
          <button type="button" onClick={() => setRevealOpen(false)} style={primaryBtn}>
            J'ai copié le secret
          </button>
        }
      >
        {reveal && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: 'rgba(255, 200, 50, 0.1)',
                border: '1px solid var(--warning)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text)',
                fontSize: 13,
              }}
            >
              <AlertTriangle size={18} style={{ color: 'var(--warning)', flexShrink: 0 }} />
              <span>
                Stockez ce secret dans le coffre-fort de l'application consommatrice. Synolog n'en garde
                qu'un hash — si vous le perdez, vous devrez générer une nouvelle paire (rotation).
              </span>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                Nom
              </label>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>{reveal.clientName}</div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                client_id (public)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <code
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    background: 'var(--glass)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 13,
                    color: 'var(--text)',
                    overflow: 'auto',
                  }}
                >
                  {reveal.clientId}
                </code>
                <button
                  type="button"
                  onClick={() => copyToClipboard(reveal.clientId)}
                  style={secondaryBtn}
                  title="Copier"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: 6,
                  fontSize: 12,
                  color: 'var(--danger)',
                  fontWeight: 600,
                }}
              >
                <Key size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                client_secret (à copier maintenant)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <code
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    background: 'var(--glass)',
                    border: '1px solid var(--danger)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 13,
                    color: 'var(--text)',
                    overflow: 'auto',
                    wordBreak: 'break-all',
                  }}
                >
                  {reveal.clientSecret}
                </code>
                <button
                  type="button"
                  onClick={() => copyToClipboard(reveal.clientSecret)}
                  style={{ ...primaryBtn, background: copied ? 'var(--success)' : 'var(--accent)' }}
                  title="Copier"
                >
                  <Copy size={14} />
                  {copied ? 'Copié' : 'Copier'}
                </button>
              </div>
            </div>

            <div
              style={{
                marginTop: 6,
                padding: 10,
                background: 'var(--glass)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                color: 'var(--text-muted)',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
              }}
            >
              {`# Exemple d'utilisation (curl)
curl -X POST https://synolog.example/api/oauth/token \\
  -d "grant_type=client_credentials" \\
  -d "client_id=${reveal.clientId}" \\
  -d "client_secret=${reveal.clientSecret}"`}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
